import { forwardRef, useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  LogOut as LogOutIcon,
  Pencil,
  Settings as SettingsIcon,
  Shield,
  Users,
} from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { updateOwnMemberProfile } from "../../api/team-api.js";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../../i18n/index.js";
import {
  LanguageOptionContent,
  languageOptionRowClassName,
} from "../common/LanguageSwitcher/LanguageSwitcher.js";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu/index.js";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet/index.js";
import { SimpleTooltip } from "../ui/tooltip/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../ui/alert-dialog/index.js";
import { Button } from "../ui/button/index.js";
import { cn } from "../../lib/utils.js";
import { markFromName } from "../../features/organizations/organization-mark.js";
import { useSuperAdminAccess } from "../../features/super-admin/use-super-admin-access.js";
import { RAIL_FOCUS, railCompactRowClassName } from "./rail-compact.js";
import { MembershipAvatarBadge } from "./RailAvatarBadge.js";
import { useMinWidth } from "./use-min-width.js";

/** Matches AuthenticatedShell tablet rail threshold — below this, mobile nav Sheet is used. */
const ACCOUNT_MENU_DROPDOWN_MIN_WIDTH_PX = 768;

const MENU_FOCUS =
  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#177e6780]";

/** Mobile sheet row: comfortable touch target, consistent gutter across every group. */
const SHEET_ROW_CLASS = cn(
  "flex min-h-11 w-full items-center gap-3 rounded-[12px] border-0 bg-transparent px-3 text-start text-[14px] font-[600] text-[#17302e] transition-colors duration-150 hover:bg-[#f3f8f6] active:bg-[#eaf4ef] [&>svg]:shrink-0 [&>svg]:text-[#6f877e]",
  MENU_FOCUS,
);

const SHEET_GROUP_LABEL_CLASS = "m-0 px-3 pt-3.5 pb-1 text-[11px] leading-4 font-[700] text-[#7c958b]";

const MENU_SURFACE =
  "font-feedbot rounded-[16px] border border-[#dce7df] bg-white p-1.5 text-[#17302e] shadow-[0_24px_60px_#06251d2e]";

const MENU_ROW =
  "group min-h-10 gap-3 rounded-[10px] px-2.5 py-2 text-[13.5px] font-[600] text-[#17302e] transition-colors duration-150 data-[highlighted]:bg-[#f3f8f6]";

const MENU_ROW_ICON =
  "shrink-0 text-[#6f877e] transition-colors duration-150 group-data-[highlighted]:text-brand-primary";

const MENU_SECTION_LABEL = "m-0 px-2.5 pt-2 pb-1 text-[11px] leading-4 font-[700] text-[#7c958b]";

const MENU_SEPARATOR = "mx-1.5 my-1.5 bg-[#e8efeb]";

function AccountIdentity({
  initials,
  displayName,
  email,
  roleLabel,
  membershipRole,
}: {
  initials: string;
  displayName: string;
  email: string;
  roleLabel: string;
  membershipRole: OrganizationMemberRoleName | null;
}): React.JSX.Element {
  return (
    <div
      className="flex min-w-0 items-center gap-3 rounded-[12px] border border-[#e1efe8] bg-[linear-gradient(140deg,#f4fbf8,#e7f5ef)] p-3"
      data-testid="account-identity"
    >
      <span className="relative inline-grid size-11 shrink-0 place-items-center rounded-[13px] bg-[linear-gradient(140deg,#5ccba4,#368a76)] text-[14px] font-[800] text-white shadow-[0_6px_16px_#368a7633]">
        {initials}
        <MembershipAvatarBadge role={membershipRole} size="card" />
      </span>
      <div className="grid min-w-0 flex-1 justify-items-start gap-0.5">
        <p className="m-0 w-full truncate text-start text-[14px] leading-5 font-[750] text-[#12372e]" title={displayName}>
          <bdi>{displayName}</bdi>
        </p>
        <p className="m-0 w-full truncate text-start text-[12px] leading-4 text-[#6b8279]">
          <bdi dir="ltr">{email}</bdi>
        </p>
        <span className="mt-1 max-w-full truncate rounded-full border border-[#cfe8dd] bg-white px-2 py-px text-[10.5px] leading-4 font-[700] text-brand-primary">
          {roleLabel}
        </span>
      </div>
    </div>
  );
}

const AccountTriggerButton = forwardRef<
  HTMLButtonElement,
  {
    compact: boolean;
    initials: string;
    displayName: string;
    roleLabel: string;
    membershipRole: OrganizationMemberRoleName | null;
    menuLabel: string;
    className?: string;
  } & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children" | "type" | "className">
>(function AccountTriggerButton(
  { compact, initials, displayName, roleLabel, membershipRole, menuLabel, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "group flex w-full min-w-0 items-center gap-[11px] rounded-[9px] border-0 bg-transparent px-1.5 py-[9px] text-start text-[#f2fff9] transition-[background-color] duration-150 ease-in-out hover:bg-white/[0.06] data-[state=open]:bg-white/[0.06]",
        RAIL_FOCUS,
        compact && "justify-center",
        className,
      )}
      data-testid="account-menu-trigger"
      {...rest}
      aria-label={menuLabel}
    >
      <span className="relative inline-grid size-[35px] shrink-0 place-items-center rounded-[11px] bg-[linear-gradient(140deg,#5ccba4,#368a76)] text-[12px] font-[800] text-white">
        {initials}
        <MembershipAvatarBadge role={membershipRole} />
      </span>
      {!compact ? (
        <>
          <span className="min-w-0 flex-1 text-start">
            <span
              className="block truncate text-start text-[12px] leading-[1.5] font-[750]"
              title={displayName}
            >
              <bdi>{displayName}</bdi>
            </span>
            <span className="block truncate text-start text-[10px] leading-[1.5] text-[#99bbb1]">
              {roleLabel}
            </span>
          </span>
          <ChevronDown
            size={16}
            strokeWidth={1.8}
            className="shrink-0 text-[#82a7a0] transition-transform duration-150 ease-in-out group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </>
      ) : null}
    </button>
  );
});

function MemberNameDialog({
  open,
  displayName,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  displayName: string;
  onOpenChange: (open: boolean) => void;
  onSaved: (displayName: string) => Promise<void> | void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState(displayName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue(displayName);
    setError(null);
  }, [open, displayName]);

  function handleOpenChange(next: boolean): void {
    if (!next && saving) return;
    if (next) {
      setValue(displayName);
      setError(null);
    }
    onOpenChange(next);
  }

  async function save(): Promise<void> {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(t("app.account.nameRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onSaved(trimmed);
      onOpenChange(false);
    } catch (caught) {
      setError(isApiError(caught) ? caught.message : t("app.account.nameError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogTitle>{t("app.account.yourName")}</AlertDialogTitle>
        <AlertDialogDescription>{t("app.account.yourNameHelp")}</AlertDialogDescription>
        <label className="grid gap-1.5">
          <span className="text-[0.75rem] font-medium text-muted">{t("app.account.yourName")}</span>
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            maxLength={80}
            className="h-9 rounded-md border border-line-strong bg-surface px-3 text-[0.8125rem] text-foreground focus:border-accent focus:shadow-[0_0_0_3px_var(--color-ring)] focus:outline-none"
            data-testid="member-name-input"
          />
        </label>
        {error ? (
          <p className="m-0 text-[0.75rem] text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <AlertDialogActions>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={saving} data-testid="member-name-save">
            {saving ? t("app.account.saving") : t("common.save")}
          </Button>
        </AlertDialogActions>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AppRailAccountMenu({
  user,
  membership,
  compact,
  onLogout,
  onDisplayNameSaved,
}: {
  user: { email: string };
  membership: {
    organizationId: number;
    role: OrganizationMemberRoleName;
    /** Real membership role; null when the organization is reachable only as Platform Admin. */
    membershipRole: OrganizationMemberRoleName | null;
    displayName: string;
    platformAdminAccess?: boolean;
  };
  compact: boolean;
  onLogout: () => void;
  onDisplayNameSaved: (organizationId: number, displayName: string) => Promise<void> | void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const languageLabelId = useId();
  const useDropdown = useMinWidth(ACCOUNT_MENU_DROPDOWN_MIN_WIDTH_PX);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const initials = markFromName(membership.displayName || user.email);
  const displayName = membership.displayName || user.email;
  const menuLabel = t("app.userMenu.label");
  const canManage = membership.role === "OWNER" || membership.role === "ADMIN";
  const superAdmin = useSuperAdminAccess();
  const roleLabel =
    membership.membershipRole === null
      ? t("organization.platformAdmin.systemManagedAccess")
      : t(`organization.role.${membership.membershipRole.toLowerCase()}`);
  const canEditName = !membership.platformAdminAccess;
  const currentLanguage: SupportedLanguage = i18n.language === "en" ? "en" : "he";

  async function saveName(next: string): Promise<void> {
    const updated = await updateOwnMemberProfile(membership.organizationId, next);
    await onDisplayNameSaved(membership.organizationId, updated.displayName);
  }

  const triggerButton = (
    <AccountTriggerButton
      compact={compact}
      initials={initials}
      displayName={displayName}
      roleLabel={roleLabel}
      membershipRole={membership.membershipRole}
      menuLabel={menuLabel}
    />
  );

  const identity = (
    <AccountIdentity
      initials={initials}
      displayName={displayName}
      email={user.email}
      roleLabel={roleLabel}
      membershipRole={membership.membershipRole}
    />
  );

  const languageRows = SUPPORTED_LANGUAGES.map((lang) => {
    const selected = lang === currentLanguage;
    return (
      <button
        key={lang}
        type="button"
        className={cn(languageOptionRowClassName(selected), "min-h-12", MENU_FOCUS)}
        aria-pressed={selected}
        data-testid={`language-option-${lang}`}
        onClick={() => void i18n.changeLanguage(lang)}
      >
        <LanguageOptionContent lang={lang} selected={selected} />
      </button>
    );
  });

  if (!useDropdown) {
    return (
      <>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>{triggerButton}</SheetTrigger>
          <SheetContent
            side="bottom"
            className="font-feedbot max-h-[min(88dvh,38rem)] gap-0 rounded-t-[22px] border-[#dce7df] bg-white p-0 text-[#17302e] shadow-[0_-18px_50px_#06251d2e]"
            aria-labelledby={titleId}
          >
            <div className="flex min-h-0 flex-col">
              <h2 id={titleId} className="sr-only">
                {menuLabel}
              </h2>
              <span
                className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-[#d5e2dc]"
                aria-hidden="true"
              />

              <div className="px-4 pt-3 pb-2">{identity}</div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
                {canEditName ? (
                  <>
                    <p className={SHEET_GROUP_LABEL_CLASS}>{t("app.account.title")}</p>
                    <button
                      type="button"
                      className={SHEET_ROW_CLASS}
                      data-testid="account-edit-profile"
                      onClick={() => {
                        setSheetOpen(false);
                        setEditing(true);
                      }}
                    >
                      <Pencil size={18} aria-hidden="true" />
                      {t("app.account.editName")}
                    </button>
                  </>
                ) : null}

                {canManage ? (
                  <>
                    <p className={SHEET_GROUP_LABEL_CLASS}>{t("app.account.workspace")}</p>
                    <button
                      type="button"
                      className={SHEET_ROW_CLASS}
                      data-testid="account-team"
                      onClick={() => {
                        setSheetOpen(false);
                        navigate("/settings/team");
                      }}
                    >
                      <Users size={18} aria-hidden="true" />
                      {t("app.account.team")}
                    </button>
                    <button
                      type="button"
                      className={SHEET_ROW_CLASS}
                      onClick={() => {
                        setSheetOpen(false);
                        navigate("/settings");
                      }}
                    >
                      <SettingsIcon size={18} aria-hidden="true" />
                      {t("app.nav.settings")}
                    </button>
                  </>
                ) : null}

                <p id={languageLabelId} className={SHEET_GROUP_LABEL_CLASS}>
                  {t("app.account.language")}
                </p>
                <div role="group" aria-labelledby={languageLabelId} className="grid gap-1">
                  {languageRows}
                </div>
              </div>

              <div className="shrink-0 border-t border-line px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
                {superAdmin ? (
                  <button
                    type="button"
                    className={cn(SHEET_ROW_CLASS, "mb-2")}
                    data-testid="account-super-admin"
                    onClick={() => {
                      setSheetOpen(false);
                      navigate("/super-admin");
                    }}
                  >
                    <Shield size={18} aria-hidden="true" />
                    {t("superAdmin.open")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={cn(
                    "flex min-h-11 w-full items-center justify-center gap-2 rounded-[12px] border border-[#f0d4cf] bg-[#fdf4f2] px-4 text-[14px] font-[700] text-[#b4413a] transition-colors duration-150 hover:bg-[#fbe9e6] active:bg-[#f9e1dd]",
                    MENU_FOCUS,
                  )}
                  data-testid="account-sign-out"
                  onClick={() => {
                    setSheetOpen(false);
                    onLogout();
                  }}
                >
                  <LogOutIcon size={18} aria-hidden="true" />
                  {t("app.signOut")}
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
        <MemberNameDialog
          open={editing}
          displayName={displayName}
          onOpenChange={setEditing}
          onSaved={saveName}
        />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        {compact ? (
          <div className={railCompactRowClassName()}>
            <SimpleTooltip content={`${displayName} · ${roleLabel}`}>
              <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
            </SimpleTooltip>
          </div>
        ) : (
          <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          aria-label={menuLabel}
          className={cn(MENU_SURFACE, "w-[min(17.5rem,calc(100vw-1.5rem))]")}
        >
          {identity}
          {canEditName || canManage || superAdmin ? (
            <div className="mt-1.5 grid gap-px">
              {canEditName ? (
                <DropdownMenuItem
                  className={MENU_ROW}
                  data-testid="account-edit-profile"
                  onSelect={() => setEditing(true)}
                >
                  <Pencil size={17} strokeWidth={1.8} className={MENU_ROW_ICON} aria-hidden="true" />
                  {t("app.account.editName")}
                </DropdownMenuItem>
              ) : null}
              {canManage ? (
                <>
                  <DropdownMenuItem
                    className={MENU_ROW}
                    data-testid="account-team"
                    onSelect={() => navigate("/settings/team")}
                  >
                    <Users size={17} strokeWidth={1.8} className={MENU_ROW_ICON} aria-hidden="true" />
                    {t("app.account.team")}
                  </DropdownMenuItem>
                  <DropdownMenuItem className={MENU_ROW} onSelect={() => navigate("/settings")}>
                    <SettingsIcon size={17} strokeWidth={1.8} className={MENU_ROW_ICON} aria-hidden="true" />
                    {t("app.nav.settings")}
                  </DropdownMenuItem>
                </>
              ) : null}
              {superAdmin ? (
                <DropdownMenuItem
                  className={MENU_ROW}
                  data-testid="account-super-admin"
                  onSelect={() => navigate("/super-admin")}
                >
                  <Shield size={17} strokeWidth={1.8} className={MENU_ROW_ICON} aria-hidden="true" />
                  {t("superAdmin.open")}
                </DropdownMenuItem>
              ) : null}
            </div>
          ) : null}
          <DropdownMenuSeparator className={MENU_SEPARATOR} />
          <p id={languageLabelId} className={MENU_SECTION_LABEL}>
            {t("app.account.language")}
          </p>
          <DropdownMenuRadioGroup
            value={currentLanguage}
            onValueChange={(lang) => void i18n.changeLanguage(lang)}
            aria-labelledby={languageLabelId}
            className="grid gap-1"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <DropdownMenuRadioItem
                key={lang}
                value={lang}
                className={languageOptionRowClassName(lang === currentLanguage)}
                data-testid={`language-option-${lang}`}
              >
                <LanguageOptionContent lang={lang} selected={lang === currentLanguage} />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator className={MENU_SEPARATOR} />
          <DropdownMenuItem
            className={cn(MENU_ROW, "text-[#b4413a] data-[highlighted]:bg-[#fdf1ef]")}
            data-testid="account-sign-out"
            onSelect={onLogout}
          >
            <LogOutIcon size={17} strokeWidth={1.8} className="shrink-0" aria-hidden="true" />
            {t("app.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <MemberNameDialog
        open={editing}
        displayName={displayName}
        onOpenChange={setEditing}
        onSaved={saveName}
      />
    </>
  );
}
